import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { listMyNotifications } from '../../services/partnerPortal';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
}

const icon = (path: string) => (
  <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d={path} />
  </svg>
);

const NAV: NavItem[] = [
  {
    to: '/partner/dashboard',
    label: 'Dashboard',
    end: true,
    icon: icon('M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'),
  },
  {
    to: '/partner/referrals',
    label: 'My referrals',
    icon: icon('M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'),
  },
  {
    to: '/partner/commissions',
    label: 'Earnings',
    icon: icon('M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'),
  },
  {
    to: '/partner/notifications',
    label: 'Notifications',
    icon: icon('M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9'),
  },
  {
    to: '/partner/profile',
    label: 'Profile',
    icon: icon('M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'),
  },
];

/**
 * Shell for the partner portal.
 *
 * Mirrors the admin console's layout so the two feel like one product, but is
 * deliberately its own component — a partner must never render admin
 * navigation, and sharing the shell would put that one prop away.
 */
const PartnerLayout: React.FC = () => {
  const { partner, signOut } = usePartnerAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const location = useLocation();

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  // Refreshed on navigation so the badge clears once the list is opened.
  useEffect(() => {
    let cancelled = false;
    listMyNotifications()
      .then((response) => {
        if (!cancelled) setUnread(response.unread);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
      isActive
        ? 'border-primary-500/30 bg-primary-500/10 text-primary-400'
        : 'border-transparent text-muted hover:bg-surface-hover hover:text-white'
    }`;

  const sidebarContent = (
    <div className="flex h-full flex-col">
      <div className="border-b border-surface-border px-5 py-5">
        <div className="flex items-center gap-3">
          <img src="/nexalogo.png" alt="Nexasolutra" className="h-8 w-8 object-contain" />
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight text-white">Nexasolutra</p>
            <p className="text-[10px] uppercase tracking-widest text-muted">Partner portal</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {NAV.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
            {item.icon}
            <span className="flex-1 truncate">{item.label}</span>
            {item.to === '/partner/notifications' && unread > 0 && (
              <span className="rounded-full bg-primary-500 px-1.5 py-0.5 text-[10px] font-medium text-white">
                {unread}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-surface-border px-3 py-4">
        <div className="px-3 pb-3">
          <p className="truncate text-sm text-white">{partner?.fullName}</p>
          <p className="truncate text-xs text-muted">{partner?.email}</p>
          {partner?.partnerId && (
            <span className="mt-2 inline-block rounded border border-primary-500/40 bg-primary-500/10 px-2 py-0.5 font-mono text-[10px] text-primary-400">
              {partner.partnerId}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={signOut}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-muted transition-colors hover:bg-surface-hover hover:text-white"
        >
          {icon('M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1')}
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-surface">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-surface-border bg-surface-card lg:flex">
        {sidebarContent}
      </aside>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <aside className="animate-slide-right relative w-64 max-w-[80%] border-r border-surface-border bg-surface-card">
            {sidebarContent}
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-surface-border bg-surface-card/95 px-4 backdrop-blur lg:hidden">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            className="-ml-2 p-2 text-muted hover:text-white"
          >
            {icon('M4 6h16M4 12h16M4 18h16')}
          </button>
          <img src="/nexalogo.png" alt="" className="h-6 w-6 object-contain" />
          <span className="text-sm font-medium text-white">Partner portal</span>
        </header>

        <main className="min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default PartnerLayout;
